export class ProductGallery
{
    static SELECTORS = {
        GALLERY: '[data-entity="product-gallery"]',
        NAV: '[data-entity="gallery-nav"]',
        PICTURES: '[data-entity="gallery-pictures"]',
        NAV_ITEM: '[data-entity="gallery-nav"] > li',
        PRODUCT_IMAGE: '.product__img'
    };

    static CSS_CLASSES = {
        ACTIVE: 'active',
        ANIMATING: 'animating',
        LAZY: 'lazy',
        LOADED: 'loaded',
        SLIDE_OUT_LEFT: 'slide-out-left',
        SLIDE_OUT_RIGHT: 'slide-out-right',
        SLIDE_IN_LEFT: 'slide-in-left',
        SLIDE_IN_RIGHT: 'slide-in-right'
    };

    static DIRECTIONS = {
        LEFT: 'left',
        RIGHT: 'right'
    };

    static ANIMATION_DURATION = 300;
    static MIN_SWIPE_DISTANCE = 50;
    

    constructor(rootNode, options = {})
    {
        this.rootNode = rootNode;

        this.options = {
            lazyload: false,
            rootMargin: '200px 0px',
            autoPreview: false,
            autoPreviewDelay: 2000,
            autoPreviewLoop: false,
            autoPreviewAnimationDuration: 1000,
            ...options
        };

        this.touch = {
            startX: 0,
            endX: 0,
            startY: 0,
            endY: 0
        };

        this.currentGallery = null;
        this.isAnimating = false;
        this.observer = null;
        this.autoPreviewTimer = null;

        this.bindEvents();

        if (this.options.lazyload) {
            this.setupIntersectionObserver();
        }

        if (this.options.autoPreview) {
            this.setupAutoPreview();
        }
    }

    setupAutoPreview()
    {
        const firstGallery = this.rootNode.querySelector(ProductGallery.SELECTORS.GALLERY);
        if (!firstGallery) {
            return;
        }

        const pictures = this.getPictures(firstGallery);
        if (pictures.length < 2) {
            return;
        }

        this.loadAllImages(firstGallery);

        const scheduleNext = () => {
            this.autoPreviewTimer = setTimeout(() => {
                const currentPictures = this.getPictures(firstGallery);
                const currentIndex = currentPictures.findIndex(
                    p => p.classList.contains(ProductGallery.CSS_CLASSES.ACTIVE)
                );

                const nextIndex = (currentIndex + 1) % currentPictures.length;
                this.switchToImage(
                    firstGallery,
                    nextIndex,
                    ProductGallery.DIRECTIONS.LEFT,
                    this.options.autoPreviewAnimationDuration
                );

                if (this.options.autoPreviewLoop) {
                    scheduleNext();
                }
            }, this.options.autoPreviewDelay);
        };

        scheduleNext();
    }

    setupIntersectionObserver()
    {
        if (!('IntersectionObserver' in window)) {
            this.rootNode
                .querySelectorAll(ProductGallery.SELECTORS.GALLERY)
                .forEach(gallery => this.loadAllImages(gallery));
            return;
        }

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadAllImages(entry.target);
                    this.observer.unobserve(entry.target);
                }
            });
        }, {
            rootMargin: this.options.rootMargin,
            threshold: 0
        });

        this.rootNode
            .querySelectorAll(ProductGallery.SELECTORS.GALLERY)
            .forEach(gallery => this.observer.observe(gallery));
    }

    observeNewGalleries(scope = this.rootNode)
    {
        if (!this.observer) {
            return;
        }

        scope
            .querySelectorAll(ProductGallery.SELECTORS.GALLERY)
            .forEach(gallery => this.observer.observe(gallery));
    }

    loadAllImages(galleryNode)
    {
        const pictures = this.getPictures(galleryNode);
        pictures.forEach(picture => this.loadImage(picture));
    }

    bindEvents()
    {
        this.rootNode.addEventListener('mouseenter', (e) => {
            if (!e.target && e.target.closest) {
                return;
            }

            const navItem = e.target.closest(ProductGallery.SELECTORS.NAV_ITEM);
            if (navItem) {
                this.handleNavItemActivation(navItem);
            }
        }, true);

        this.rootNode.addEventListener('mouseleave', (e) => {
            if (!e.target.classList.contains('product__img')) {
                return;
            }
        }, true);

        this.rootNode.addEventListener('touchstart', (e) => {
            const gallery = e.target.closest(ProductGallery.SELECTORS.GALLERY);
            if (!gallery) {
                return;
            }

            this.currentGallery = gallery;
            this.touch.startX = e.changedTouches[0].screenX;
            this.touch.startY = e.changedTouches[0].screenY;
        }, { passive: true });

        this.rootNode.addEventListener('touchend', (e) => {
            if (!this.currentGallery || this.isAnimating) {
                return;
            }

            this.touch.endX = e.changedTouches[0].screenX;
            this.touch.endY = e.changedTouches[0].screenY;
            
            this.handleSwipe();
            this.currentGallery = null;
        }, { passive: true });
    }

    handleSwipe()
    {
        const diffX = this.touch.startX - this.touch.endX;
        const diffY = Math.abs(this.touch.startY - this.touch.endY);

        if (Math.abs(diffX) < ProductGallery.MIN_SWIPE_DISTANCE || diffY > Math.abs(diffX)) {
            return;
        }

        this.cancelAutoPreview();

        const galleryNode = this.currentGallery;
        const pictures = this.getPictures(galleryNode);
        const currentIndex = pictures.findIndex(p => p.classList.contains(ProductGallery.CSS_CLASSES.ACTIVE));

        let newIndex;

        if (diffX > 0) {
            newIndex = (currentIndex + 1) % pictures.length;
        } else {
            newIndex = currentIndex - 1 < 0 ? pictures.length - 1 : currentIndex - 1;
        }

        this.switchToImage(
            galleryNode,
            newIndex,
            diffX > 0 ? ProductGallery.DIRECTIONS.LEFT : ProductGallery.DIRECTIONS.RIGHT
        );
    }

    switchToImage(galleryNode, index, direction = null, animationDuration = ProductGallery.ANIMATION_DURATION)
    {
        const navItems = this.getNavItems(galleryNode);
        const pictures = this.getPictures(galleryNode);

        if (index < 0 || index >= pictures.length) {
            return;
        }

        const currentPicture = pictures.find(p => p.classList.contains(ProductGallery.CSS_CLASSES.ACTIVE));
        const newPicture = pictures[index];

        navItems.forEach((item, i) => {
            item.classList.toggle(ProductGallery.CSS_CLASSES.ACTIVE, i === index);
        });

        this.loadImage(newPicture);

        if (direction && currentPicture && newPicture && currentPicture !== newPicture) {
            this.isAnimating = true;

            galleryNode.style.setProperty('--gallery-animation-duration', `${animationDuration}ms`);

            newPicture.classList.add(ProductGallery.CSS_CLASSES.ANIMATING);
            currentPicture.classList.add(ProductGallery.CSS_CLASSES.ANIMATING);

            void newPicture.offsetWidth;

            const slideOutClass = direction === ProductGallery.DIRECTIONS.LEFT
                ? ProductGallery.CSS_CLASSES.SLIDE_OUT_LEFT
                : ProductGallery.CSS_CLASSES.SLIDE_OUT_RIGHT;
            const slideInClass = direction === ProductGallery.DIRECTIONS.LEFT
                ? ProductGallery.CSS_CLASSES.SLIDE_IN_RIGHT
                : ProductGallery.CSS_CLASSES.SLIDE_IN_LEFT;

            currentPicture.classList.add(slideOutClass);
            newPicture.classList.add(slideInClass);

            setTimeout(() => {
                currentPicture.classList.remove(
                    ProductGallery.CSS_CLASSES.ACTIVE,
                    ProductGallery.CSS_CLASSES.ANIMATING,
                    slideOutClass
                );

                newPicture.classList.add(ProductGallery.CSS_CLASSES.ACTIVE);
                newPicture.classList.remove(
                    ProductGallery.CSS_CLASSES.ANIMATING,
                    slideInClass
                );

                galleryNode.style.removeProperty('--gallery-animation-duration');

                this.isAnimating = false;
            }, animationDuration);
        } else {
            pictures.forEach((picture, i) => {
                picture.classList.toggle(ProductGallery.CSS_CLASSES.ACTIVE, i === index);
            });
        }
    }

    handleNavItemActivation(navItem)
    {
        const galleryNode = navItem.closest(ProductGallery.SELECTORS.GALLERY);
        if (!galleryNode) {
            return;
        }

        this.cancelAutoPreview();

        const navItems = this.getNavItems(galleryNode);
        const index = navItems.indexOf(navItem);

        if (index === -1) {
            return;
        }

        this.switchToImage(galleryNode, index);
    }

    loadImage(pictureNode) 
    {
        if (!pictureNode) {
            return;
        }

        const img = pictureNode.querySelector('img');
        const sources = pictureNode.querySelectorAll('source[data-srcset]');

        if (img && !img.classList.contains(ProductGallery.CSS_CLASSES.LAZY)) {
            return;
        }

        sources.forEach(source => {
            if (source.dataset.srcset) {
                source.srcset = source.dataset.srcset;
                delete source.dataset.srcset;
            }
        });

        if (img) {
            if (img.dataset.src) {
                img.src = img.dataset.src;
                delete img.dataset.src;
            }

            if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
                delete img.dataset.srcset;
            }

            img.classList.remove(ProductGallery.CSS_CLASSES.LAZY);
            img.classList.add(ProductGallery.CSS_CLASSES.LOADED);
        }
    }

    resetGallery(galleryNode)
    {
        const navItems = this.getNavItems(galleryNode);
        const pictures = this.getPictures(galleryNode);

        pictures.forEach((picture, index) => {
            picture.classList.toggle(ProductGallery.CSS_CLASSES.ACTIVE, index === 0);
        });

        navItems.forEach((item, index) => {
            item.classList.toggle(ProductGallery.CSS_CLASSES.ACTIVE, index === 0);
        });
    }

    cancelAutoPreview()
    {
        if (this.autoPreviewTimer !== null) {
            clearTimeout(this.autoPreviewTimer);
            this.autoPreviewTimer = null;
        }
    }

    getNavItems(galleryNode)
    {
        return [...galleryNode.querySelector(ProductGallery.SELECTORS.NAV)?.children || []];
    }

    getPictures(galleryNode)
    {
        return [...galleryNode.querySelector(ProductGallery.SELECTORS.PICTURES)?.children || []];
    }
}